import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ListChecks,
  Tag,
  Route,
  Wrench,
  FileType,
} from "lucide-react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";

// ─── API Helper (بنفس أسلوب PlanAdmin.jsx) ──────────────────────────────────
const BASE_URL = process.env.REACT_APP_API_URL || "/api";

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(data.message || `حدث خطأ غير متوقع (status: ${res.status})`);
  return data;
}

const LookupAPI = {
  getAll: (type) => apiRequest(`/lookups/${type}`),
  create: (type, name) =>
    apiRequest(`/lookups/${type}`, { method: "POST", body: JSON.stringify({ name }) }),
  update: (type, id, name) =>
    apiRequest(`/lookups/${type}/${id}`, { method: "PUT", body: JSON.stringify({ name }) }),
  remove: (type, id) => apiRequest(`/lookups/${type}/${id}`, { method: "DELETE" }),
};

// ─── تعريف القوائم المعروضة ──────────────────────────────────────────────────
const LOOKUP_TYPES = [
  { key: "issue-categories", label: "فئات المشاكل", icon: Tag },
  { key: "issue-channels", label: "قنوات الأعطال", icon: Route },
  { key: "solution-mechanisms", label: "آليات الحل", icon: Wrench },
  { key: "document-types", label: "أنواع المستندات", icon: FileType },
];

// ─── مدير قائمة واحدة (إضافة / تعديل / حذف) ─────────────────────────────────
function LookupManager({ type, label }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await LookupAPI.getAll(type);
      setItems(data);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      setSaving(true);
      await LookupAPI.create(type, newName.trim());
      setNewName("");
      await load();
    } catch (err) {
      alert("فشلت الإضافة: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditingName(item.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async () => {
    if (!editingName.trim()) return;
    try {
      setSaving(true);
      await LookupAPI.update(type, editingId, editingName.trim());
      cancelEdit();
      await load();
    } catch (err) {
      alert("فشل التحديث: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف هذا العنصر؟")) return;
    try {
      setSaving(true);
      await LookupAPI.remove(type, id);
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6" dir="rtl">
      {/* إضافة عنصر جديد */}
      <div className="flex items-center gap-3 mb-5">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder={`إضافة ${label} جديد...`}
          className="flex-1 px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all text-right"
        />
        <button
          onClick={handleAdd}
          disabled={saving || !newName.trim()}
          className="bg-[#546E7A] hover:bg-[#263238] text-white font-ibm px-6 py-3 rounded-2xl font-bold flex items-center gap-2 transition shadow-lg shadow-[#546e7a20] justify-center active:scale-95 disabled:opacity-50 shrink-0"
        >
          <Plus size={18} /> إضافة
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm text-right">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-gray-400 text-sm">جاري التحميل...</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">لا توجد عناصر بعد</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-gray-50 hover:bg-gray-100 transition"
            >
              {editingId === item.id ? (
                <>
                  <input
                    autoFocus
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    className="flex-1 px-3 py-2 rounded-xl border border-[#546E7A] bg-white outline-none text-right"
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="p-2 rounded-xl bg-green-100 text-green-600 hover:bg-green-200 transition"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-2 rounded-xl bg-gray-200 text-gray-500 hover:bg-gray-300 transition"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-[#263238] font-medium">{item.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => startEdit(item)}
                      className="p-2 rounded-xl text-gray-400 hover:bg-blue-50 hover:text-[#546E7A] transition"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-500 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────
const Settings = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024);
  const [activeType, setActiveType] = useState(LOOKUP_TYPES[0].key);

  useEffect(() => {
    document.title = "إعدادات - الإدارة العامة للعمليات والتوقيع الإلكتروني";
  }, []);

  const activeLabel = LOOKUP_TYPES.find((t) => t.key === activeType)?.label;

  return (
    <div className="flex min-h-screen bg-[#F8F9FA] font-ibm" dir="rtl">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} />

      <div
        className={`flex-1 transition-all duration-300 p-4 md:p-8 ${isSidebarOpen ? "lg:mr-64" : "lg:mr-20"}`}
      >
        <Header isSidebarOpen={isSidebarOpen} setSidebarOpen={setSidebarOpen} title="الإعدادات" />

        {/* Hero Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6 text-right">
          <div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#546E7A]">
              إعدادات النظام
            </h2>
            <p className="text-gray-400 mt-1">
              إدارة القوائم المرجعية المستخدمة في باقي شاشات النظام
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Tabs */}
          <div className="flex flex-wrap gap-0 px-6 pt-5 border-b border-gray-100 overflow-x-auto">
            {LOOKUP_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveType(t.key)}
                  className={`px-5 py-3 text-sm font-medium transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeType === t.key
                      ? "text-[#546E7A] border-[#546E7A]"
                      : "text-gray-400 border-transparent hover:text-gray-600"
                  }`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </div>

          <LookupManager key={activeType} type={activeType} label={activeLabel} />
        </div>

        {/* تنويه بسيط لما يستقر باقي التوسعات */}
        <div className="mt-6 flex items-start gap-3 bg-white rounded-2xl border border-gray-100 p-5 text-right">
          <div className="bg-[#546E7A]/10 p-2 rounded-xl shrink-0">
            <ListChecks className="text-[#546E7A]" size={20} />
          </div>
          <div>
            <p className="text-sm font-bold text-[#263238]">
              إدارة المستخدمين والأدوار والإدارات
            </p>
            <p className="text-xs text-gray-400 mt-1">
              متاحة حاليًا من صفحتي "المستخدمين" و"الإدارات" في القائمة الجانبية، وتغيير كلمة
              المرور متاح من القائمة المنسدلة أعلى الصفحة.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;